import { Box, Grid, Typography } from '@mui/material';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { BiLocationPlus, BiCalendar, BiFile } from 'react-icons/bi';
import { csrfTokenCall, ILocation } from 'wisestep-api';
import {
  WsFilterDialog,
  WsColors,
  WsLocationSearch,
  Label,
  WsTagsList,
  ChipsType,
  WsTypography,
  WsTypoVariants,
  WsElementWithPopOver,
} from 'wisestep-base';
import { WsSearchFilter } from 'wisestep-website/src/components';

import { visaOptions } from '../../../helper/VisaOptions';
import { IHotJobsSearchFilters } from '../../types/IHotJobsSearch';

interface HotJobsSearchProps {
  openAdvancedFilters: Dispatch<SetStateAction<boolean>>;
  performSearch: (data) => void;
  onReset: () => void;
}

const HotJobsSearchFilter = ({
  openAdvancedFilters,
  performSearch,
  onReset,
}: HotJobsSearchProps) => {
  const [filtersArray, setFiltersArray] = useState<string[]>([]);
  const [token, setToken] = useState<string>('');
  const [auth, setAuth] = useState<string>('');
  const methods = useFormContext<IHotJobsSearchFilters>();
  const postedInBetweenDays = [1, 7, 15, 30, 45, 60, 90, 180, 360];
  const { handleSubmit, watch, setValue } = methods;
  const locations: ILocation[] | undefined = watch('locations');
  useEffect(() => {
    const getToken = async () => {
      const res = await csrfTokenCall();
      const token = res.config.headers?.['X-XSRF-TOKEN'];
      const auth = res.config.headers?.['Authorization'];
      setToken(token ?? '');
      setAuth(auth ?? '');
      return token;
    };
    getToken();
  }, []);
  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit(performSearch)}>
        <WsSearchFilter
          name="hotJobsFilter"
          showDefaultRightActions={true}
          showLeftActions={true}
          showChildren={false}
          onClickFilter={() => openAdvancedFilters(true)}
          clearAllFields={() => onReset()}
          leftActionsChildren={
            <Grid container pt={4} height="52px">
              <Grid item xs={12} container>
                <Grid item xs={4} pt={0.5}>
                  <WsFilterDialog
                    label="Location"
                    name={locations?.length ? `Selected ${locations?.length}` : 'All'}
                    icon={<BiLocationPlus size={26} color={WsColors.TheBlues500} />}
                  >
                    <Grid px={1} py={2} item xs={4} minWidth={480}>
                      <WsLocationSearch
                        name="locations"
                        placeHolder="Search Location"
                        size="small"
                        wisestepApi={true}
                        token={token}
                        authorization={auth}
                      />
                    </Grid>
                  </WsFilterDialog>
                </Grid>
                <Box pt={1}>
                  <BiCalendar size={26} color={WsColors.TheBlues500} />
                </Box>
                <Grid pl={1} item xs={3}>
                  <Typography color={WsColors.TertiaryShades600} fontSize="10px" fontWeight="bold">
                    Posted In
                  </Typography>
                  <Box>
                    <WsElementWithPopOver
                      element={
                        <WsTypography variant={WsTypoVariants.BODY2} cursor="pointer">
                          {watch('postedInBetweenDays') + ' Days' ?? 'Posted In'}
                        </WsTypography>
                      }
                    >
                      <Grid rowGap={3}>
                        {postedInBetweenDays.map((e, index) => (
                          <Box
                            key={index}
                            p={1}
                            sx={{
                              '&:hover': {
                                background: WsColors.TheBlues500,
                              },
                            }}
                          >
                            <WsTypography
                              onClick={() => setValue('postedInBetweenDays', e)}
                              variant={WsTypoVariants.SUB_TITLE}
                              cursor="pointer"
                            >
                              {e + ' Days'}
                            </WsTypography>
                          </Box>
                        ))}
                      </Grid>
                    </WsElementWithPopOver>
                  </Box>
                </Grid>
                <Box pt={1}>
                  <BiFile size={26} color={WsColors.TheBlues500} />
                </Box>
                <Grid pl={1} item xs={4}>
                  <Typography color={WsColors.TertiaryShades600} fontSize="10px" fontWeight="bold">
                    Visa Status
                  </Typography>
                  <Box>
                    <WsElementWithPopOver
                      element={
                        <WsTypography variant={WsTypoVariants.BODY2} cursor="pointer">
                          {watch('visaStatus') ?? 'Visa Status'}
                        </WsTypography>
                      }
                    >
                      <Grid rowGap={3}>
                        {visaOptions.map((e, index) => (
                          <Box
                            key={index}
                            p={1}
                            sx={{
                              '&:hover': {
                                background: WsColors.TheBlues500,
                              },
                            }}
                          >
                            <WsTypography
                              onClick={() => setValue('visaStatus', String(e.value))}
                              variant={WsTypoVariants.SUB_TITLE}
                              cursor="pointer"
                            >
                              {e.label}
                            </WsTypography>
                          </Box>
                        ))}
                      </Grid>
                    </WsElementWithPopOver>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          }
        />
      </Box>

      {filtersArray.length > 0 && (
        <Box>
          <Label sx={{ fontWeight: 'bold' }}>Results generated for:</Label>
          <WsTagsList
            chipArray={filtersArray}
            count={100}
            type={ChipsType.PRIMARY}
            allowDelete={true}
            onDelete={(chip) => {
              setFiltersArray((prev) => {
                prev.splice(prev.indexOf(chip), 1);
                return [...prev];
              });
            }}
          />
        </Box>
      )}
    </FormProvider>
  );
};

export default HotJobsSearchFilter;
